import { history, IRoute, IRouteComponentProps, useDispatch } from 'umi';
import {
  ComponentWrapper,
  Feedback,
  MediaContextProvider,
  NavigationHeader,
  NoAccessPage,
} from '@digit-fe/digit-component';
import cx from 'classnames';
import { useEffect, useMemo, useRef } from 'react';
import { useModel } from '@@/plugin-model/useModel';
import { ConfigProvider, message } from 'antd';
import { clearCookie, delCookie, loginCas } from '@/utils';
import styles from './index.less';
import Cookies from 'js-cookie';
import SecondaryNavigation from '@/components/secondaryNavigation';
import Breads from '@/components/breads';
import locale from 'antd/locale/zh_CN';
import dayjs from 'dayjs';

import 'dayjs/locale/zh-cn';

dayjs.locale('zh-cn');

export default function BaseLayout(props: IRouteComponentProps) {
  const dispatch = useDispatch();
  const { children } = props;
  const { initialState } = useModel('@@initialState');

  useEffect(() => {
    if (initialState) {
      dispatch({
        type: 'userInfo/save',
        payload: initialState,
      });
      initialStateRef.current = initialState;
    }
  }, [initialState]);

  const uid = initialState?.uid;

  const secondaryNavigationList = useMemo(() => {
    const menus = [
      {
        name: '首页',
        link: '/v2/camp',
        invisible: false,
      },
    ];
    if (
      initialState.permission?.superAdmin ||
      initialState.permission?.manageSomeCorpTraining ||
      initialState?.permission.manageCorpTraining
    ) {
      menus.push({
        name: '企业学习管理',
        link: `${process.env.EDU_URL}/corptraining/console`,
        invisible: false,
      });
    }
    if (
      initialState.permission?.superAdmin ||
      initialState.permission?.createCamp ||
      initialState.permission?.manageCamp ||
      initialState.permission?.manageSomeCamp
    ) {
      menus.push({
        name: '打卡营管理',
        link: '/v2/console/campsetings',
        invisible: false,
      });
    }
    if (initialState.permission?.superAdmin || initialState.permission?.manageQuestionBank) {
      menus.push({
        name: '题库管理',
        link: '/v2/console/questionbank',
        invisible: false,
      });
    }
    if (initialState.permission?.superAdmin) {
      menus.push({
        name: '权限管理',
        link: '/v2/console/access',
        invisible: false,
      });
    }
    return menus;
  }, [initialState]);

  const ref = useRef<any>();
  const initialStateRef = useRef(initialState);
  const createMenu = () => {
    const navMenu: IRoute[] = [];
    const routes = props.route.routes;
    routes?.forEach((route) => {
      route.routes?.forEach((item) => {
        if (item.nav) {
          navMenu.push(item);
        }
      });
    });
    return navMenu.map((item, index) => ({
      id: String(index),
      title: item.navTitle,
      path: item.path as string,
    }));
  };

  const renderContent = () => {
    const permission: Record<string, any> = initialState?.permission || {};
    const path = location.pathname;

    if (permission.superAdmin) {
      return (
        <>
          <Breads route={props.route} />
          <div className={styles.content_wrapper}>{children}</div>
        </>
      );
    }

    const routePerms: { match: string; required: string[] }[] = [
      {
        match: '/v2/console/campsetings',
        required: ['createCamp', 'manageCamp', 'manageSomeCamp'],
      },
      { match: '/v2/console/questionbank', required: ['manageQuestionBank'] },
      { match: '/v2/console/access', required: ['superAdmin'] },
      {
        match: '/corptraining/console',
        required: ['superAdmin', 'manageSomeCorpTraining', 'manageCorpTraining'],
      },
    ];

    const matched = routePerms.find((r) => path.includes(r.match));
    const hasAuth = !matched || matched.required.some((key) => Boolean(permission[key]));

    return hasAuth ? (
      <>
        <Breads route={props.route} />
        <div className={styles.content_wrapper}>{children}</div>
      </>
    ) : (
      <NoAccessPage isFrAccount={true} />
    );
  };

  // @ts-ignore
  return (
    <>
      <MediaContextProvider>
        <div
          style={{
            position: 'sticky',
            top: '0px',
            height: 'auto',
            zIndex: '1',
          }}
        >
          <NavigationHeader
            searchComponent={false}
            className={cx(styles.layout)}
            navItemComponent={
              <SecondaryNavigation
                list={secondaryNavigationList.filter((item) => !item.invisible)}
              />
            }
            ref={ref}
            env={process.env.UMI_ENV === 'product' ? 'prod' : 'test'}
            menu={createMenu()}
            siteName={'学习管理后台'}
            uid={uid === 0 ? undefined : String(uid)}
            username={initialState?.username}
            onMonitorChange={(data) => {
              console.log('监听到了通行证数据');
              console.log(data);
              if (!data?.uid) {
                delCookie('fr_token');
                message.success('您已成功退出登录');
                window.location.href = `${process.env.ID_URL}/login/token/logout?referrer=${window.location.href}`;
              } else {
                if (process.env.UMI_ENV !== 'dev') {
                  if (!initialStateRef?.current?.uid) {
                    loginCas({
                      env: process.env.UMI_ENV === 'product' ? 'prod' : 'test',
                    });
                  }
                }
              }
            }}
            onLogoClick={() => {
              window.location.href = 'https://home.fanruan.com';
            }}
            onLogin={() => {
              loginCas({
                env: process.env.UMI_ENV === 'product' ? 'prod' : 'test',
              });
            }}
            onLogout={() => {
              Cookies.remove('fr_token_test', {
                domain: '.fanruan.com',
                path: '/',
              });
              Cookies.remove('fr_token_test', {
                domain: window.location.hostname,
                path: '/',
              });
              clearCookie();
              message.success('您已成功退出登录');
              window.location.href = `${process.env.ID_URL}/login/token/logout?referrer=${window.location.href}`;
            }}
            onMenuClick={(data) => {
              history.push(data.path);
            }}
          />
        </div>
        <div className={styles.page_wrapper}>
          <div className={styles.page}>
            <ConfigProvider locale={locale}>{renderContent()}</ConfigProvider>
          </div>

          <ComponentWrapper>
            <Feedback
              uid={uid}
              env={process.env.UMI_ENV === 'product' ? 'prod' : 'test'}
              style={{ zIndex: 2 }}
              onDataChange={(isFiners, uid, token, mobile) => {
                const tempMobile = mobile || '';
                if (isFiners) {
                  return `https://pe9qbh7c.shuzhiniao.com/web/#/h5/business/brd?embedded=true&modal=true&hideRootMenu=true&spaceId=11093&%E7%B1%BB%E5%9E%8B=37047&%E5%8F%8D%E9%A6%88%E4%BA%BAUID=${uid}&%E9%A1%B5%E9%9D%A2URL=${encodeURIComponent(
                    window.location.href,
                  )}&%E5%8F%8D%E9%A6%88%E4%BA%BA%E6%89%8B%E6%9C%BA%E5%8F%B7=${tempMobile}&hiddenFields=%E9%A1%B5%E9%9D%A2URL%2C%E5%8F%8D%E9%A6%88%E4%BA%BAUID%2C%E7%B1%BB%E5%9E%8B%2C%E5%A4%84%E7%90%86%E4%BA%BA%E5%9B%9E%E5%A4%8D%2C%E5%8F%8D%E9%A6%88%E4%BA%BA%E6%89%8B%E6%9C%BA%E5%8F%B7%2C%E6%A8%A1%E5%9D%97`;
                }
                return `https://pe9qbh7c.shuzhiniao.com/web/#/ticket/a2555f84970f418a98960adb4029aed1/form?embedded=true&hiddenFields=%E9%A1%B5%E9%9D%A2URL%2C%E5%8F%8D%E9%A6%88%E4%BA%BAUID%2C%E5%A4%84%E7%90%86%E4%BA%BA%E5%9B%9E%E5%A4%8D&ticketToken=${token}`;
              }}
            />
          </ComponentWrapper>
        </div>
      </MediaContextProvider>
    </>
  );
}
